import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import type { ReactElement } from "react";
import { GeneralV3Response, LoadingType } from "../../../types/CommonTypes";
import { useAppSelector } from "../redux-hooks";
import { activateProductItems, createProducts, deleteProductPrice, deleteProductPurchasePrice, editProductDocumentStatus, fetchProductKitByProductItemIds, getActiveProductPurchasePrices, getInactivatedProductDocuments, getProductDocumentsByProductId, getProductItem, getProductItemsHistory, getProductPurchasePricingHistory as getProductPurchasePricingHistoryApi, getProductQuestionsByProductId, getProductsPricingHistory, getSafeguardQuestionsByProductId, inactivateProductDocument, inactivateProductItems, inactivateProductPrices, inactivateProductPurchasePrice, productDocumentActivation, productItemsInProviderTemplateByPatient, ProductConfigRules, saveProductDocument, saveProductItems, saveProductPriceItems, saveProductPurchasePrice, saveProductPurchasePriceItems, saveProductsItemsPrices, saveProductsLogo, updateProducts, getProductShippingConfigByProductId, saveProductShippingConfig, SaveProductShippingConfigRequest, deleterefProductbyproductItemKitItemId, fetchProductKitByProductItemId, saveproductkit } from "../../../service/ProductService";
import { updateElementValue } from "../../../utils/updateElementValue";

export type ProductDocumentListInfo = {
    entityId: string;
    productTemplateId: number;
    type: string;
    documentType: string;
    entityName: string;
    docName: string;
    description: string;
    comments: string | ReactElement;
    documentURL: string | ReactElement | null;
    productId: number;
    status: string | ReactElement;
    manufacturer: any;
    inactiveDate?: string | null;
};

export type ProductKitItemRequest = {
    productItemId: number;
    refProductId: number;
    refProductItemId: number;
    qty: number;
};

export type ProductKitCreateObj = {
    productKitItemRequests?: ProductKitItemRequest[];
    [key: string]: any;
};

export type ProductDocument = {
    documentType: string;
    effectiveDate : string;
    productDocumentInfo: {
        documentURL: string
    }
    name: string;
    attachmentProductDocument: {
      documentName: string;
      content: string;
      fileType: string;
    }[];
    productId: number | null;
    product: string | null;
    manufactureId: number | null;
    manufacturer: {
        manufacturerName: string
        manufacturerId: number | null
    }
    comments: string;
    status: string;
    category: string | null;
    subCategory: string | null;
    publish: boolean;
    productDocumentId: number | null,
    documentId: number | null,
    editProductDocument: boolean,
    externalLinkType: string

  }

export type ProductInfo = {
    saveMode: string,
    productId: number,
    productName: string,
    hcpcsCode: string | null,
    inactiveDate: any,
    manufacturerId: number,
    // agreementTemplate: string,
    productDescription: string | null,
    composition: string | null,
    /** CS-2073 — sterile | non-sterile | null */
    compoundType: string | null,
    insuranceCoverage: string | null,
    productLogo: string,
    // CS-1760 - isAgreementRequired removed, now driven by Product Config AGREEMENT MODE
    // isAgreementRequired: boolean,
    mues: string,
    documents: DocumentItemInfo[],
    productItems: ProductItemInfo[],
    productPrices: any , //ProductItemPriceInfo[],
    productPurchasePrices?: ProductPurchasePriceInfo[],
    productCategory: string,
    productStatus: string,
    purchaseAndBill?: string | null, // CS-1765
    productDocumentStatus?: string[],
    hasSKUPrice: boolean,
    isKit: boolean;
    configRules?: ProductConfigRules[];
}

export type ProductItemInfo = {
    productItemId: number;
    productId: number;
    sku: string;
    size: string;
    units: string;
    inactiveDate: any;
    isSizeActive: boolean;
    productItemPriceId?: number;
    effectiveDate?: any;
    allowableUnitPrice?: number | "";
    listPrice?: number | "";
    customPrice?: number | "";
    priceType?: string;
    hcpcsCode?:string|null
    usageType?: string
}

export type ProductItemInProviderTemplate = {
    dressingType : string,
	productItemId: number,
    productId: number,
    sku: string,
    productItemDescription: string,
    practiceProductId: number,
    manufacturerId: number,
    incisionId: string
}

export type ProductItemPriceRequest = {
    productPriceId: number;
    productItemId: number;
    sku: string;
    effectiveDate: any;
    inactiveDate: any;
    allowableUnitPrice: number | "";
    listPrice: number | "";
    customPrice?: number | "";
}

export type ProductItemPriceInfo = {
    productPriceId: number;
    productId: number;
    effectiveDate: any;
    inactiveDate: any;
    allowableUnitPrice: number | "";
    macId: number;
    priceType: string;
    listPrice: number | "";
    customPrice?: number | "";
    /** Optional. { Tier1: 100, Tier2: 200 }. {} when no tier price is entered. */
    tierPrices?: Record<string, number>;
    practiceProducts?: PracticeProductInfo[],
    fakeValidationPurposeAttribute?: string;
    productItem?: ProductItemInfo;
}

export type ProductPurchasePriceInfo = {
    productPurchasePriceId: number;
    productId: number;
    productItemId?: number | null;
    effectiveDate: any;
    inactiveDate: any;
    purchasePrice: number | "";
    sku?: string;
    productItem?: ProductItemInfo;
};

export type ProductItemPurchasePriceRequest = {
    productId: number;
    productItemPurchasePrices: ProductPurchasePriceInfo[];
};

export type ProductItemsPriceInfo = {
    productId: number;
    ProductItemPrices: any[];
}

export type PracticeProductInfo = {
    practiceProductId: number;
    productId: number;
    productPriceId: number;
    productName: string;
    practiceName: string;
    providerName: string;
    agreementStatus: string;
    auditTimeStamp: string;
    fileType: string;
    effectiveDate: string;
    inactiveDate: string;
    discountType: string;
    extendedPrice: number;
}

export type DocumentItemInfo = {
    documentId: number;
    documentName: string;
    type: string;
    fileType: string;
    content: string;
}

export type UpdateProductDocumentStatusRequest = {
    productId: number;
    documentId: number;
    manufacturerId?: number;
    status?: string;
    documentType?: string;
    docName?: string;
};

export type ProductKitInfo = {
    productItemKitItemId: number;
    productItemId: number;
    refProductId: number;
    productName: string; 
    qty: number;
    size: string;
  };
  
  export type ProductkitItemInfo = {
    productId: productOption | null;
    graftQty: GraftQty[];
  };
  
  export type productOption = {
    label: string;
    value: string | number;
    productId: string | undefined | number;
  };
  
  export type GraftQty = {
    productItemId: number;
    quantity: number;
    graftsNeeded: string;
  };
  
  
  export const initialValuesOfProductkitItem: ProductkitItemInfo = {
    productId: null,
    graftQty: [],
  };
  
  
export type ProductInfoState = {
    productObject: ProductInfo,
    manufacturerList: any,
    productPricingHistory: ProductItemPriceInfo[],
    productPurchasePrices: ProductPurchasePriceInfo[],
    productPurchasePricingHistory: ProductPurchasePriceInfo[],
    productItemsHistory: ProductItemInfo[],
    productItemsInProviderTemplateByPatient: ProductItemInProviderTemplate[],
    productDocumentInfo: ProductDocumentListInfo[],
    inactivatedProductDocuments: ProductDocumentListInfo[],
    ProductKitListInfo: ProductKitInfo[] | null,
    createProductStatus: LoadingType,
    getProductStatus: LoadingType,
    saveProductItemStatus: LoadingType,
    saveProductPriceStatus: LoadingType,
    inactivateProductItemStatus: LoadingType,
    activateProductItemStatus: LoadingType,
    inactivateProductPriceStatus: LoadingType,
    deleteProductPriceStatus: LoadingType,
    fetchProductPriceHistoryStatus: LoadingType,
    saveProductPurchasePriceStatus: LoadingType,
    inactivateProductPurchasePriceStatus: LoadingType,
    deleteProductPurchasePriceStatus: LoadingType,
    fetchProductPurchasePriceHistoryStatus: LoadingType,
    fetchActiveProductPurchasePricesStatus: LoadingType,
    fetchProductItemHistoryStatus: LoadingType,
    updateProductStatus: LoadingType,
    saveProductLogoStatus: LoadingType,
    fetchProductDocumentsStatus: LoadingType,
    uploadProductDocumentStatus: LoadingType,
    inactivateProductDocumentStatus: LoadingType,
    getInactivatedProductDocumentsStatus: LoadingType,
    activateProductDocumentStatus: LoadingType,
    updateStatusOfProductDocumentStatus: LoadingType,
    ProductkitListFetchStatus: LoadingType,
    getProductQuestionsStatus: LoadingType,
    getSafeguardQuestionsStatus: LoadingType,
    productItemsInProviderTemplateByPatientStatus: LoadingType;
    productQuestions: any,
    safeguardQuestions: any,
    // CS-1766 - Product Shipping Config
    productShippingConfig: any[],
    fetchProductShippingConfigStatus: LoadingType,
    saveProductShippingConfigStatus: LoadingType
}
export type RecordPastOrder = {
    OrderPlaceDate: string;
}

export type InactivateProductDocumentInfo = {
    productId?: number | null,
    manufacturerId?: number | null,
    documentId: number | string,
    inactiveDate: string
}

export const initialValuesOfProductItemPrice = {
    productPriceId: 0,
    effectiveDate: null,
    inactiveDate: null,
    allowableUnitPrice: "",
    macId: 0,
    priceType: "",
    listPrice: "",
    customPrice: "",
    fakeValidationPurposeAttribute: "",
} as ProductItemPriceInfo;

export const initialValuesOfProductPurchasePrice = {
    productPurchasePriceId: 0,
    productId: 0,
    productItemId: null,
    effectiveDate: null,
    inactiveDate: null,
    purchasePrice: "",
} as ProductPurchasePriceInfo;

// export const initialValuesOfProductItemsPrice = {
//     productPriceId: 0,
//     productItemId: 0,
//     sku: "",
//     effectiveDate: null,
//     allowableUnitPrice: "",
//     listPrice: "",
//     customPrice: undefined,
// } as ProductItemPriceRequest;

export const initialValuesOfProductItemsPrice = {
    productId: 0,
    ProductItemPrices: []
} as ProductItemsPriceInfo;

export const initialValuesOfProductItem = {
    productItemId: 0,
    sku: "",
    size: "",
    units: "",
    inactiveDate: null,
    isSizeActive: true,
    priceType: "NATIONAL"
} as ProductItemInfo;

export const initialValuesOfDocumentItem = {
    documentId: 0,
    documentName: "",
    type: "",
    fileType: "",
    content: "",
} as DocumentItemInfo;

const initialState = {
    productObject: {
        saveMode: "",
        productId : 0,
        productName: "",
        hcpcsCode: "",
        inactiveDate: "",
        manufacturerId: 0,
        // agreementTemplate: "",
        productDescription: "",
        composition: "",
        compoundType: null,
        insuranceCoverage: "",
        productLogo: "",
        // CS-1760 - isAgreementRequired removed
        // isAgreementRequired: false,
        mues: "",
        documents: [],
        productItems: [],
        productPrices: [],
        productCategory: "",
        productDocuments: [],
        productStatus: "",
        purchaseAndBill: null, // CS-1765
        ProductDocumentStatus: [],
        hasSKUPrice: false,
        isKit: false
    },
    manufacturerList: [],
    productPricingHistory: [],
    productPurchasePrices: [],
    productPurchasePricingHistory: [],
    productItemsHistory: [],
    productItemsInProviderTemplateByPatient: [],
    productDocumentInfo: [],
    inactivatedProductDocuments: [],
    ProductKitListInfo: null,
    createProductStatus: "idle" ,
    getProductStatus: "idle",
    saveProductItemStatus: "idle",
    saveProductPriceStatus: "idle",
    inactivateProductItemStatus: "idle",
    activateProductItemStatus: "idle",
    inactivateProductPriceStatus: "idle",
    deleteProductPriceStatus: "idle",
    fetchProductPriceHistoryStatus: "idle",
    saveProductPurchasePriceStatus: "idle",
    inactivateProductPurchasePriceStatus: "idle",
    deleteProductPurchasePriceStatus: "idle",
    fetchProductPurchasePriceHistoryStatus: "idle",
    fetchActiveProductPurchasePricesStatus: "idle",
    fetchProductItemHistoryStatus: "idle",
    updateProductStatus: "idle",
    saveProductLogoStatus: 'idle',
    fetchProductDocumentsStatus: 'idle',
    uploadProductDocumentStatus:'idle',
    inactivateProductDocumentStatus: 'idle',
    getInactivatedProductDocumentsStatus: 'idle',
    activateProductDocumentStatus: 'idle',
    updateStatusOfProductDocumentStatus: 'idle',
    ProductkitListFetchStatus: 'idle',
    getProductQuestionsStatus: 'idle',
    getSafeguardQuestionsStatus: 'idle',
    productItemsInProviderTemplateByPatientStatus: 'idle',
    productQuestions: undefined,
    safeguardQuestions: null,
    // CS-1766 - Product Shipping Config
    productShippingConfig: [],
    fetchProductShippingConfigStatus: 'idle',
    saveProductShippingConfigStatus: 'idle'
    
} as ProductInfoState;

export const createProduct = createAsyncThunk<
    any,
    { product: ProductInfo, navigate?: any },
    { rejectValue: string }
>("createProduct", async (data, { rejectWithValue }) => {
    const { product, navigate } = data;
    try {
        const response = await createProducts(product);
        let resp = response.data;
        // Optional navigate keeps legacy list redirect when a caller passes navigate.
        if (navigate) {
            navigate("/products/explore_market");
        }
        // navigate("/products_list")
        return resp;
    }
    catch (error) {
        //@ts-ignore
        return rejectWithValue(error?.data);
    }
});

export const getProduct = createAsyncThunk<
    any,
    { productId: number },
    { rejectValue: string }
>("getProduct", async (data, { rejectWithValue }) => {
    const { productId } = data;
    try {
        const response = await getProductItem(productId);
        let resp = response.data;
        return resp;
    }
    catch (error) {
        //@ts-ignore
        return rejectWithValue(error?.data);
    }
});

export const saveProductItem = createAsyncThunk<
    any,
    { productItem: ProductItemInfo },
    { rejectValue: string }
>("saveProductItem", async (data, { rejectWithValue }) => {
    const { productItem } = data;
    try {
        const response = await saveProductItems(productItem);
        let resp = response.data;
        return resp;
    }
    catch (error) {
        //@ts-ignore
        return rejectWithValue(error?.data);
    }
});

export const saveProductPriceItem = createAsyncThunk<
    any,
    { productPriceItem: ProductItemsPriceInfo },
    { rejectValue: string }
>("saveProductPriceItem", async (data, { rejectWithValue }) => {
    const { productPriceItem } = data;
    try {
        const response = await saveProductPriceItems(productPriceItem);
        let resp = response.data;
        return resp;
    }
    catch (error) {
        //@ts-ignore
        return rejectWithValue(error?.data);
    }
});

export const inactivateProductItem = createAsyncThunk<
    any,
    { productItemId: number, date: string },
    { rejectValue: string }
>("inactivateProductItem", async (data, { rejectWithValue }) => {
    const { productItemId, date } = data;
    try {
        const response = await inactivateProductItems(productItemId, date);
        let resp = response.data;
        return resp;
    }
    catch (error) {
        //@ts-ignore
        return rejectWithValue(error?.data);
    }
});

export const activateProductItem = createAsyncThunk<
    any,
    { productItemId: number},
    { rejectValue: string }
>("activateProductItem", async (data, { rejectWithValue }) => {
    const { productItemId } = data;
    try {
        const response = await activateProductItems(productItemId);
        let resp = response.data;
        return resp;
    }
    catch (error) {
        //@ts-ignore
        return rejectWithValue(error?.data);
    }
});

export const inactivateProductPrice = createAsyncThunk<
    any,
    { productPriceId: number, date: string },
    { rejectValue: string }
>("inactivateProductPrice", async (data, { rejectWithValue }) => {
    const { productPriceId, date } = data;
    try {
        const response = await inactivateProductPrices(productPriceId, date);
        let resp = response.data;
        return resp;
    }
    catch (error) {
        //@ts-ignore
        return rejectWithValue(error?.data);
    }
});

export const deleteProductPriceObj = createAsyncThunk<
    any,
    { productPriceId: number },
    { rejectValue: string }
>("deleteProductPriceObj", async (data, { rejectWithValue }) => {
    const { productPriceId } = data;
    try {
        const response = await deleteProductPrice(productPriceId);
        let resp = response.data;
        return resp;
    } catch (error) {
        console.log("deleteProductPriceObj==slice====eeeeee");
        console.log(error);
        //@ts-ignore
        return rejectWithValue(error?.data);
    }
});

export const getProductPricingHistory = createAsyncThunk<
    any,
    { productId: number },
    { rejectValue: string }
>("getProductPricingHistory", async (data, { rejectWithValue }) => {
    const { productId } = data;
    try {
        const response = await getProductsPricingHistory(productId);
        let resp = response.data;
        return resp;
    }
    catch (error) {
        //@ts-ignore
        return rejectWithValue(error?.data);
    }
});

export const fetchActiveProductPurchasePrices = createAsyncThunk<
    any,
    { productId: number },
    { rejectValue: string }
>("fetchActiveProductPurchasePrices", async (data, { rejectWithValue }) => {
    try {
        const response = await getActiveProductPurchasePrices(data.productId);
        return response.data;
    } catch (error) {
        //@ts-ignore
        return rejectWithValue(error?.data);
    }
});

export const saveProductPurchasePriceItem = createAsyncThunk<
    any,
    { purchasePrice: ProductPurchasePriceInfo },
    { rejectValue: string }
>("saveProductPurchasePriceItem", async (data, { rejectWithValue }) => {
    try {
        const response = await saveProductPurchasePrice(data.purchasePrice);
        return response.data;
    } catch (error) {
        //@ts-ignore
        return rejectWithValue(error?.data);
    }
});

export const saveAllProductItemsPurchasePrices = createAsyncThunk<
    any,
    { purchasePriceItems: ProductItemPurchasePriceRequest },
    { rejectValue: string }
>("saveAllProductItemsPurchasePrices", async (data, { rejectWithValue }) => {
    try {
        const response = await saveProductPurchasePriceItems(data.purchasePriceItems);
        return response.data;
    } catch (error) {
        //@ts-ignore
        return rejectWithValue(error?.data);
    }
});

export const inactivateProductPurchasePriceItem = createAsyncThunk<
    any,
    { productPurchasePriceId: number; date: string },
    { rejectValue: string }
>("inactivateProductPurchasePriceItem", async (data, { rejectWithValue }) => {
    try {
        const response = await inactivateProductPurchasePrice(data.productPurchasePriceId, data.date);
        return response.data;
    } catch (error) {
        //@ts-ignore
        return rejectWithValue(error?.data);
    }
});

export const deleteProductPurchasePriceObj = createAsyncThunk<
    any,
    { productPurchasePriceId: number },
    { rejectValue: string }
>("deleteProductPurchasePriceObj", async (data, { rejectWithValue }) => {
    try {
        const response = await deleteProductPurchasePrice(data.productPurchasePriceId);
        return response.data;
    } catch (error) {
        //@ts-ignore
        return rejectWithValue(error?.data);
    }
});

export const getProductPurchasePricingHistory = createAsyncThunk<
    any,
    { productId: number },
    { rejectValue: string }
>("getProductPurchasePricingHistory", async (data, { rejectWithValue }) => {
    try {
        const response = await getProductPurchasePricingHistoryApi(data.productId);
        return response.data;
    } catch (error) {
        //@ts-ignore
        return rejectWithValue(error?.data);
    }
});

export const getProductItemHistory = createAsyncThunk<
    any,
    { productId: number },
    { rejectValue: string }
>("getProductItemHistory", async (data, { rejectWithValue }) => {
    const { productId } = data;
    try {
        const response = await getProductItemsHistory(productId);
        let resp = response.data;
        return resp;
    }
    catch (error) {
        //@ts-ignore
        return rejectWithValue(error?.data);
    }
});

export const saveProductLogo = createAsyncThunk<
    any,
    { logoData: Object },
    { rejectValue: string }
>("saveProductLogo", async (data, { rejectWithValue }) => {
    const { logoData } = data;
    try {
        const response = await saveProductsLogo(logoData);
        let resp = response.data;
        return resp;
    }
    catch (error) {
        //@ts-ignore
        return rejectWithValue(error?.data);
    }
});

export const updateProductDetails = createAsyncThunk<
    any,
    { product: Object },
    { rejectValue: string }
>("updateProductDetails", async (data, { rejectWithValue }) => {
    const { product } = data;
    try {
        const response = await updateProducts(product);
        let resp = response.data;
        return resp;
    }
    catch (error) {
        //@ts-ignore
        return rejectWithValue(error?.data);
    }
});

export const fetchProductDocumentsByProductId = createAsyncThunk("getProductDocumentsList", async (productId: number) => {
    const response = await getProductDocumentsByProductId(productId);
    let resp = response.data;
    return resp;
});

export const uploadProductDocument = createAsyncThunk<
    any,
    { productDocument: ProductDocument },
    { rejectValue: string }
>("uploadProductDocument", async (data, { rejectWithValue }) => {
    const { productDocument } = data;
    try {
        const response = await saveProductDocument(productDocument);
        let resp = response.data;
        return resp;
    }
    catch (error) {
        //@ts-ignore
        return rejectWithValue(error?.data);
    }
});

export const inactiveProductDocument = createAsyncThunk<
    any,
    { inactiveRequest: InactivateProductDocumentInfo },
    { rejectValue: string }
>("inactiveProductDocument", async (data, { rejectWithValue }) => {
    const { inactiveRequest } = data;
    try {
        const response = await inactivateProductDocument(inactiveRequest);
        let resp = response.data;
        return resp;
    }
    catch (error) {
        //@ts-ignore
        return rejectWithValue(error?.data);
    }
});

export const getInactiveProductDocuments = createAsyncThunk<
    any,
    { productId: number },
    { rejectValue: string }
>("getInactiveProductDocuments", async (data, { rejectWithValue }) => {
    const { productId } = data;
    try {
        const response = await getInactivatedProductDocuments(productId);
        let resp = response.data;
        return resp;
    }
    catch (error) {
        //@ts-ignore
        return rejectWithValue(error?.data);
    }
});


export const activateProductDocument = createAsyncThunk<
    any,
    { activateRequest: UpdateProductDocumentStatusRequest },
    { rejectValue: string }
>("activateProductDocument", async (data, { rejectWithValue }) => {
    const { activateRequest } = data;
    try {
        const response = await productDocumentActivation(activateRequest);
        let resp = response.data;
        return resp;
    }
    catch (error) {
        //@ts-ignore
        return rejectWithValue(error?.data);
    }
});

export const updateStatusOfProductDocument = createAsyncThunk<
    any,
    { updateRequest: UpdateProductDocumentStatusRequest },
    { rejectValue: string }
>("updateProductDocumentStatus", async (data, { rejectWithValue }) => {
    const { updateRequest } = data;
    try {
        const response = await editProductDocumentStatus(updateRequest);
        let resp = response.data;
        return resp;
    }
    catch (error) {
        //@ts-ignore
        return rejectWithValue(error?.data);
    }
});

export const saveAllProductItemsPrices = createAsyncThunk<
    any,
    { productPriceItems: ProductItemsPriceInfo },
    { rejectValue: string }
>("saveAllProductItemsPrices", async (data, { rejectWithValue }) => {
    const { productPriceItems } = data;
    try {
        const response = await saveProductsItemsPrices(productPriceItems);
        let resp = response.data;
        return resp;
    }
    catch (error) {
        //@ts-ignore
        return rejectWithValue(error?.data);
    }
});

//collagen kit task cs-715 start here
export const getProductItemkitItem = createAsyncThunk<any, { productItemId: number, skipLoader?: boolean }>("getProductItemkitItem", async ({productItemId, skipLoader= false}, { rejectWithValue }) => {
    try {
        const response = await fetchProductKitByProductItemId(productItemId, { 'x-skip-loader': skipLoader.toString() });
        return response.data;
    } catch (error: any) {
        return rejectWithValue(error?.message || 'An error occurred');
    }
});

export const getProductItemkitItemByIds = createAsyncThunk<
    any,
    { productItemIds: number[] },
    { rejectValue: string }
>("getProductItemkitItemByIds", async (data, { rejectWithValue }) => {
    const { productItemIds } = data;
    try {
        const response = await fetchProductKitByProductItemIds(productItemIds);
        let resp = response.data;
        return resp;
    }
    catch (error) {
        //@ts-ignore
        return rejectWithValue(error?.data);
    }
});


        export const savePoductKit = createAsyncThunk(
        'savePoductKit',
        async (data: ProductKitCreateObj, { rejectWithValue }) => {
            try {
            const response = await saveproductkit(data);
            return response.data;
            } catch (error: any) {
            return rejectWithValue(error?.data || 'Failed to save data.');
            }
        }
        );


        export const deleterefProductbyproduciKitId = createAsyncThunk(
        'deleterefProductbyproduciKitId',
        async (productItemKitItemId: number) => {
            const response = await deleterefProductbyproductItemKitItemId(productItemKitItemId);
            return response.data;
        }
        );

////collagen kit task cs-715 end here

export const getProductQuestions = createAsyncThunk("getProductQuestions", async ( productId: number ) => {
    try {
        const response = await getProductQuestionsByProductId(productId);
        let resp = response.data;
        return resp;
    }
    catch (error) {
        //@ts-ignore
        return rejectWithValue(error?.data);
    }
});

export const getSafeguardQuestions = createAsyncThunk("getSafeguardQuestions", async ( productId: number ) => {
    try {
        const response = await getSafeguardQuestionsByProductId(productId);
        let resp = response.data;
        return resp;
    }
    catch (error) {
        //@ts-ignore
        return rejectWithValue(error?.data);
    }
});


export const getProductItemsInProviderTemplateByPatient = createAsyncThunk<
    any,
    { patientId: number }
>("getProductItemsInProviderTemplateByPatient", async (data, { rejectWithValue }) => {
    const { patientId } = data;
    try {
        const response = await productItemsInProviderTemplateByPatient(patientId);
        let resp = response.data;
        console.log(resp)
        return resp;
    }
    catch (error) {
        //@ts-ignore
        return rejectWithValue(error?.data);
    }
});

// CS-1766 - Fetch product shipping config by product ID
export const fetchProductShippingConfig = createAsyncThunk<
    any,
    { productId: number },
    { rejectValue: string }
>("fetchProductShippingConfig", async (data, { rejectWithValue }) => {
    const { productId } = data;
    try {
        const response = await getProductShippingConfigByProductId(productId);
        let resp = response.data;
        return resp;
    }
    catch (error) {
        //@ts-ignore
        return rejectWithValue(error?.data);
    }
});
// CS-1766 - Save product shipping config
export const saveProductShippingConfigThunk = createAsyncThunk<
    any,
    { payload: SaveProductShippingConfigRequest },
    { rejectValue: string }
>("saveProductShippingConfig", async (data, { rejectWithValue }) => {
    const { payload } = data;
    try {
        const response = await saveProductShippingConfig(payload);
        let resp = response.data;
        return resp;
    }
    catch (error) {
        //@ts-ignore
        return rejectWithValue(error?.data);
    }
});
// CS-1766 END

const productSlice = createSlice({
    name: "product",
    initialState,
    reducers: {
        updateElementInProductSlice: updateElementValue,
        updateProductObjectValues: (state: ProductInfoState, action) => {
            state.productObject = action.payload;
        },
        resetProductListData: (state: ProductInfoState) => {
            return { ...state, ...initialState };
      },
        resetProductQuestions: (state: ProductInfoState) => {
            state.productQuestions = [];
        },
        resetSafeguardQuestions: (state: ProductInfoState) => {
            state.safeguardQuestions = [];
        },
        resetFetchStatus: (state) =>{
            state.createProductStatus= "idle";
            state.getProductStatus= "idle";
            state.saveProductItemStatus= "idle";
            state.saveProductPriceStatus= "idle";
            state.inactivateProductItemStatus= "idle";
            state.activateProductItemStatus= "idle";
            state.inactivateProductPriceStatus= "idle";
            state.deleteProductPriceStatus= "idle";
            state.fetchProductPriceHistoryStatus= "idle";
            state.fetchProductItemHistoryStatus= "idle";
            state.updateProductStatus= "idle";
            state.saveProductLogoStatus= 'idle';
            state.uploadProductDocumentStatus='idle';
            state.inactivateProductDocumentStatus = 'idle';
            state.getInactivatedProductDocumentsStatus = 'idle';
            state.activateProductDocumentStatus = 'idle';
            state.updateStatusOfProductDocumentStatus = 'idle';
            state.ProductkitListFetchStatus = 'idle';
            state.fetchProductShippingConfigStatus = 'idle'; // CS-1766
            state.saveProductShippingConfigStatus = 'idle'; // CS-1766
        },
        resetCreateProductSlice: (state) => initialState
    },
    extraReducers: (builder) => {
        builder
            .addCase(createProduct.pending, (state) => {
                state.createProductStatus = "loading";
            })
            .addCase(createProduct.fulfilled, (state, action: PayloadAction<GeneralV3Response>) => {
                state.createProductStatus = "success";
            })
            .addCase(createProduct.rejected, (state, action) => {
                state.createProductStatus = 'error';
            })
            .addCase(getProduct.pending, (state) => {
                state.getProductStatus = "loading";
            })
            .addCase(getProduct.fulfilled, (state, action: PayloadAction<GeneralV3Response>) => {
                state.getProductStatus = "success";
                state.productObject = action.payload?.data;
            })
            .addCase(getProduct.rejected, (state, action) => {
                state.getProductStatus = 'error';
            })
            .addCase(saveProductItem.pending, (state) => {
                state.saveProductItemStatus = "loading";
            })
            .addCase(saveProductItem.fulfilled, (state, action: PayloadAction<GeneralV3Response>) => {
                state.saveProductItemStatus = "success";
                state.productObject.productItems = action.payload?.data;

            })
            .addCase(saveProductItem.rejected, (state, action) => {
                state.saveProductItemStatus = 'error';
            })
            .addCase(inactivateProductItem.pending, (state) => {
                state.inactivateProductItemStatus = "loading";
            })
            .addCase(inactivateProductItem.fulfilled, (state, action: PayloadAction<GeneralV3Response>) => {
                state.inactivateProductItemStatus = "success";
                state.productObject.productItems = action.payload?.data;

            })
            .addCase(inactivateProductItem.rejected, (state, action) => {
                state.inactivateProductItemStatus = 'error';
            })
            .addCase(activateProductItem.pending, (state) => {
                state.activateProductItemStatus = "loading";
            })
            .addCase(activateProductItem.fulfilled, (state, action: PayloadAction<GeneralV3Response>) => {
                state.activateProductItemStatus = "success";
                state.productObject.productItems = action.payload?.data;
            })
            .addCase(activateProductItem.rejected, (state, action) => {
                state.activateProductItemStatus = 'error';
            })
            .addCase(saveProductPriceItem.pending, (state) => {
                state.saveProductPriceStatus = "loading";
            })
            .addCase(saveProductPriceItem.fulfilled, (state, action: PayloadAction<GeneralV3Response>) => {
                state.saveProductPriceStatus = "success";
                state.productObject.productPrices = action.payload?.data
            })
            .addCase(saveProductPriceItem.rejected, (state, action) => {
                state.saveProductPriceStatus = 'error';
            })
            .addCase(inactivateProductPrice.pending, (state) => {
                state.inactivateProductPriceStatus = "loading";
            })
            .addCase(inactivateProductPrice.fulfilled, (state, action: PayloadAction<GeneralV3Response>) => {
                state.inactivateProductPriceStatus = "success";
                state.productObject.productPrices = action.payload?.data
            })
            .addCase(inactivateProductPrice.rejected, (state, action) => {
                state.inactivateProductPriceStatus = 'error';
            })
            .addCase(deleteProductPriceObj.pending, (state) => {
                state.deleteProductPriceStatus = "loading";
            })
            .addCase(deleteProductPriceObj.fulfilled, (state, action: PayloadAction<GeneralV3Response>) => {
                state.deleteProductPriceStatus = "success";
                state.productObject.productPrices = action.payload?.data
            })
            .addCase(deleteProductPriceObj.rejected, (state, action) => {
                state.deleteProductPriceStatus = 'error';
            })
            .addCase(getProductPricingHistory.pending, (state) => {
                state.fetchProductPriceHistoryStatus = "loading";
            })
            .addCase(getProductPricingHistory.fulfilled, (state, action: PayloadAction<GeneralV3Response>) => {
                state.fetchProductPriceHistoryStatus = "success";
                state.productPricingHistory = action.payload?.data;
            })
            .addCase(getProductPricingHistory.rejected, (state, action) => {
                state.fetchProductPriceHistoryStatus = 'error';
            })
            .addCase(fetchActiveProductPurchasePrices.pending, (state) => {
                state.fetchActiveProductPurchasePricesStatus = "loading";
            })
            .addCase(fetchActiveProductPurchasePrices.fulfilled, (state, action: PayloadAction<GeneralV3Response>) => {
                state.fetchActiveProductPurchasePricesStatus = "success";
                state.productPurchasePrices = action.payload?.data || [];
            })
            .addCase(fetchActiveProductPurchasePrices.rejected, (state) => {
                state.fetchActiveProductPurchasePricesStatus = 'error';
            })
            .addCase(saveProductPurchasePriceItem.pending, (state) => {
                state.saveProductPurchasePriceStatus = "loading";
            })
            .addCase(saveProductPurchasePriceItem.fulfilled, (state, action: PayloadAction<GeneralV3Response>) => {
                state.saveProductPurchasePriceStatus = "success";
                state.productPurchasePrices = action.payload?.data || [];
            })
            .addCase(saveProductPurchasePriceItem.rejected, (state) => {
                state.saveProductPurchasePriceStatus = 'error';
            })
            .addCase(saveAllProductItemsPurchasePrices.pending, (state) => {
                state.saveProductPurchasePriceStatus = "loading";
            })
            .addCase(saveAllProductItemsPurchasePrices.fulfilled, (state, action: PayloadAction<GeneralV3Response>) => {
                state.saveProductPurchasePriceStatus = "success";
                state.productPurchasePrices = action.payload?.data || [];
            })
            .addCase(saveAllProductItemsPurchasePrices.rejected, (state) => {
                state.saveProductPurchasePriceStatus = 'error';
            })
            .addCase(inactivateProductPurchasePriceItem.pending, (state) => {
                state.inactivateProductPurchasePriceStatus = "loading";
            })
            .addCase(inactivateProductPurchasePriceItem.fulfilled, (state, action: PayloadAction<GeneralV3Response>) => {
                state.inactivateProductPurchasePriceStatus = "success";
                state.productPurchasePrices = action.payload?.data || [];
            })
            .addCase(inactivateProductPurchasePriceItem.rejected, (state) => {
                state.inactivateProductPurchasePriceStatus = 'error';
            })
            .addCase(deleteProductPurchasePriceObj.pending, (state) => {
                state.deleteProductPurchasePriceStatus = "loading";
            })
            .addCase(deleteProductPurchasePriceObj.fulfilled, (state, action: PayloadAction<GeneralV3Response>) => {
                state.deleteProductPurchasePriceStatus = "success";
                state.productPurchasePrices = action.payload?.data || [];
            })
            .addCase(deleteProductPurchasePriceObj.rejected, (state) => {
                state.deleteProductPurchasePriceStatus = 'error';
            })
            .addCase(getProductPurchasePricingHistory.pending, (state) => {
                state.fetchProductPurchasePriceHistoryStatus = "loading";
            })
            .addCase(getProductPurchasePricingHistory.fulfilled, (state, action: PayloadAction<GeneralV3Response>) => {
                state.fetchProductPurchasePriceHistoryStatus = "success";
                state.productPurchasePricingHistory = action.payload?.data || [];
            })
            .addCase(getProductPurchasePricingHistory.rejected, (state) => {
                state.fetchProductPurchasePriceHistoryStatus = 'error';
            })
            .addCase(getProductItemHistory.pending, (state) => {
                state.fetchProductItemHistoryStatus = "loading";
            })
            .addCase(getProductItemHistory.fulfilled, (state, action: PayloadAction<GeneralV3Response>) => {
                state.fetchProductItemHistoryStatus = "success";
                state.productItemsHistory = action.payload?.data;
            })
            .addCase(getProductItemHistory.rejected, (state, action) => {
                state.fetchProductItemHistoryStatus = 'error';
            })
            .addCase(updateProductDetails.pending, (state) => {
                state.updateProductStatus = "loading";
            })
            .addCase(updateProductDetails.fulfilled, (state, action: PayloadAction<GeneralV3Response>) => {
                state.updateProductStatus = "success";
            })
            .addCase(updateProductDetails.rejected, (state, action) => {
                state.updateProductStatus = 'error';
            })
            .addCase(saveProductLogo.pending, (state) => {
                state.saveProductLogoStatus = "loading";
            })
            .addCase(saveProductLogo.fulfilled, (state, action: PayloadAction<GeneralV3Response>) => {
                state.saveProductLogoStatus = "success";
            })
            .addCase(saveProductLogo.rejected, (state, action) => {
                state.saveProductLogoStatus = 'error';
            })
            .addCase(fetchProductDocumentsByProductId.pending, (state) => {
                state.fetchProductDocumentsStatus = "loading";
            })
            .addCase(fetchProductDocumentsByProductId.fulfilled, (state, action) => {
                state.fetchProductDocumentsStatus = "success";
                // state.productDocumentInfo = action.payload.data;
                state.productDocumentInfo = action.payload?.data; // CS-1984
            })
            .addCase(fetchProductDocumentsByProductId.rejected, (state) => {
                state.fetchProductDocumentsStatus = "error";
            })
            .addCase(uploadProductDocument.pending, (state) => {
                state.uploadProductDocumentStatus = "loading";
            })
            .addCase(uploadProductDocument.fulfilled, (state, action) => {
                state.uploadProductDocumentStatus = "success";
            })
            .addCase(uploadProductDocument.rejected, (state) => {
                state.uploadProductDocumentStatus = "error";
            })
            .addCase(inactiveProductDocument.pending, (state) => {
                state.inactivateProductDocumentStatus = "loading";
            })
            .addCase(inactiveProductDocument.fulfilled, (state, action) => {
                state.inactivateProductDocumentStatus = "success";
            })
            .addCase(inactiveProductDocument.rejected, (state) => {
                state.inactivateProductDocumentStatus = "error";
            })
            .addCase(getInactiveProductDocuments.pending, (state) => {
                state.getInactivatedProductDocumentsStatus = "loading";
            })
            .addCase(getInactiveProductDocuments.fulfilled, (state, action) => {
                state.getInactivatedProductDocumentsStatus = "success";
                state.inactivatedProductDocuments = action.payload?.data;
            })
            .addCase(getInactiveProductDocuments.rejected, (state) => {
                state.getInactivatedProductDocumentsStatus = "error";
            })
            .addCase(activateProductDocument.pending, (state) => {
                state.activateProductDocumentStatus = "loading";
            })
            .addCase(activateProductDocument.fulfilled, (state, action) => {
                state.activateProductDocumentStatus = "success";
            })
            .addCase(activateProductDocument.rejected, (state) => {
                state.activateProductDocumentStatus = "error";
            })
            .addCase(updateStatusOfProductDocument.pending, (state) => {
                state.updateStatusOfProductDocumentStatus = "loading";
            })
            .addCase(updateStatusOfProductDocument.fulfilled, (state, action) => {
                state.updateStatusOfProductDocumentStatus = "success";
            })
            .addCase(updateStatusOfProductDocument.rejected, (state) => {
                state.updateStatusOfProductDocumentStatus = "error";
            })
            .addCase(saveAllProductItemsPrices.pending, (state) => {
                state.saveProductPriceStatus = "loading";
            })
            .addCase(saveAllProductItemsPrices.fulfilled, (state, action: PayloadAction<GeneralV3Response>) => {
                state.saveProductPriceStatus = "success";
                state.productObject.productPrices = action.payload?.data
            })
            .addCase(getProductItemkitItem.pending, (state) => {
                state.ProductkitListFetchStatus = "loading";
            })
            .addCase(getProductItemkitItem.fulfilled, (state, action: PayloadAction<GeneralV3Response>) => {
                state.ProductkitListFetchStatus = "success";
                state.ProductKitListInfo = action.payload?.data;
            })
            .addCase(getProductItemkitItem.rejected, (state) => {
                state.ProductkitListFetchStatus = 'error';
            })
            .addCase(savePoductKit.pending, (state) => {
                state.ProductkitListFetchStatus = "loading";
            })
            .addCase(savePoductKit.fulfilled, (state, action: PayloadAction<GeneralV3Response>) => {
                state.ProductkitListFetchStatus = "success";
                state.ProductKitListInfo = action.payload?.data;
            })
            .addCase(savePoductKit.rejected, (state) => {
                state.ProductkitListFetchStatus = 'error';
            })
            .addCase(deleterefProductbyproduciKitId.pending, (state) => {
                state.ProductkitListFetchStatus = "loading";
            })
            .addCase(deleterefProductbyproduciKitId.fulfilled, (state, action: PayloadAction<GeneralV3Response>) => {
                state.ProductkitListFetchStatus = "success";
                state.ProductKitListInfo = action.payload?.data;
            })
            .addCase(deleterefProductbyproduciKitId.rejected, (state) => {
                state.ProductkitListFetchStatus = 'error';
            })
            .addCase(saveAllProductItemsPrices.rejected, (state, action) => {
                state.saveProductPriceStatus = 'error';
            })
            .addCase(getProductQuestions.pending, (state) => {
                state.getProductQuestionsStatus = "loading";
            })
            .addCase(getProductQuestions.fulfilled, (state, action) => {
                state.getProductQuestionsStatus = "success";
                state.productQuestions = action.payload?.data;
            })
            .addCase(getProductQuestions.rejected, (state) => {
                state.getProductQuestionsStatus = "error";
            })
            .addCase(getSafeguardQuestions.pending, (state) => {
                state.getSafeguardQuestionsStatus = "loading";
            })
            .addCase(getSafeguardQuestions.fulfilled, (state, action) => {
                state.getSafeguardQuestionsStatus = "success";
                state.safeguardQuestions = action.payload?.data;
            })
            .addCase(getSafeguardQuestions.rejected, (state) => {
                state.getSafeguardQuestionsStatus = "error";
            })
            .addCase(getProductItemsInProviderTemplateByPatient.pending, (state) => {
                state.productItemsInProviderTemplateByPatientStatus = "loading";
            })
            .addCase(getProductItemsInProviderTemplateByPatient.fulfilled, (state, action) => {
                state.productItemsInProviderTemplateByPatientStatus = "success";
                state.productItemsInProviderTemplateByPatient = action.payload?.data;
            })
            .addCase(getProductItemsInProviderTemplateByPatient.rejected, (state) => {
                state.productItemsInProviderTemplateByPatientStatus = "error";
            })
            // CS-1766 - Product Shipping Config
            .addCase(fetchProductShippingConfig.pending, (state) => {
                state.fetchProductShippingConfigStatus = "loading";
            })
            .addCase(fetchProductShippingConfig.fulfilled, (state, action) => {
                state.fetchProductShippingConfigStatus = "success";
                state.productShippingConfig = action.payload?.data;
            })
            .addCase(fetchProductShippingConfig.rejected, (state) => {
                state.fetchProductShippingConfigStatus = "error";
            })
            // CS-1766 - Save Product Shipping Config
            .addCase(saveProductShippingConfigThunk.pending, (state) => {
                state.saveProductShippingConfigStatus = "loading";
            })
            .addCase(saveProductShippingConfigThunk.fulfilled, (state, action) => {
                state.saveProductShippingConfigStatus = "success";
            })
            .addCase(saveProductShippingConfigThunk.rejected, (state) => {
                state.saveProductShippingConfigStatus = "error";
            });
            // CS-1766 END
    },
});

export const { updateElementInProductSlice, updateProductObjectValues, resetCreateProductSlice, resetFetchStatus, resetSafeguardQuestions, resetProductQuestions } = productSlice.actions;
export const useProductReducer = (): ProductInfoState =>
    useAppSelector((state) => state.productSlice as ProductInfoState);
// export const useProductKitListSlice = () => useAppSelector((state) => state.productKitList);
export default productSlice.reducer;
